import {
  AlignmentObject,
  Answer,
  EducationalOrganization,
  FAQPage,
  Question,
  Thing,
  WithContext,
} from "schema-dts";

import { BASE_URL } from "@/constants/base-url";
import { Deck } from "@/types/flashcards";

export const AUDIENCE_TYPE = "Students";

export const COMMON_PROPERTIES = {
  audience: {
    "@type": "Audience",
    audienceType: AUDIENCE_TYPE,
  },
  inLanguage: "en-US",
  isAccessibleForFree: true,
} as const;

export const organization: EducationalOrganization = {
  "@type": "EducationalOrganization",
  areaServed: "Worldwide",
  description:
    "Master any subject with AI-powered practice tests, flashcards, personalized tutoring, and interactive learning tools. Free practice tests for hundreds of subjects.",
  foundingDate: "2007",
  logo: `${BASE_URL}/icon-225x225.png`,
  name: "Varsity Tutors",
  sameAs: [
    "https://www.facebook.com/VarsityTutors",
    "https://twitter.com/VarsityTutors",
    "https://www.linkedin.com/company/varsity-tutors",
    "https://www.instagram.com/varsitytutors/",
  ],
  url: "https://www.varsitytutors.com",
};

export const createEducationalProperties = (
  options: {
    alignment?: AlignmentObject;
    educationalLevel?: string;
    educationalUse?: string;
    learningResourceType?: string;
    subject?: string;
  } = {}
) => ({
  educationalLevel: options.educationalLevel || "Elementary to High School",
  educationalUse: options.educationalUse || "StudyAid",
  learningResourceType: options.learningResourceType || "Flashcard",
  ...(options.subject && { subject: options.subject }),
  ...(options.alignment && { educationalAlignment: options.alignment }),
  ...COMMON_PROPERTIES,
});

export const createLearningResource = (
  name: string,
  description: string,
  url: string,
  options: {
    alignment?: AlignmentObject;
    author?: string;
    dateModified?: string;
    datePublished?: string;
    educationalLevel?: string;
    educationalUse?: string;
    learningResourceType?: string;
    subject?: string;
  } = {}
): WithContext<Thing> => ({
  "@context": "https://schema.org",
  "@type": "LearningResource",
  description,
  educationalLevel: options.educationalLevel || "Elementary to High School",
  educationalUse: options.educationalUse || "StudyAid",
  learningResourceType: options.learningResourceType || "InteractiveResource",
  name,
  url,
  ...(options.subject && { about: options.subject }),
  ...(options.alignment && { educationalAlignment: options.alignment }),
  ...(options.author && {
    author: { "@type": "Person", name: options.author },
  }),
  ...(options.datePublished && { datePublished: options.datePublished }),
  ...(options.dateModified && { dateModified: options.dateModified }),
  ...COMMON_PROPERTIES,
});

export const createQuestion = (
  name: string,
  answerText: string,
  options: {
    alignment?: AlignmentObject;
    educationalLevel?: string;
    educationalUse?: string;
    learningResourceType?: string;
    subject?: string;
  } = {}
): Question => ({
  "@type": "Question",
  acceptedAnswer: {
    "@type": "Answer",
    text: answerText.replace(/\$/g, ""),
  } as Answer,
  educationalLevel: options.educationalLevel || "Elementary to High School",
  educationalUse: options.educationalUse || "StudyAid",
  learningResourceType: options.learningResourceType || "Flashcard",
  name: name.replace(/\$/g, ""), // Remove LaTeX delimiters
  ...(options.subject && { subject: options.subject }),
  ...(options.alignment && { educationalAlignment: options.alignment }),
});

export const getFlashcardDeckData = (deck: Deck): WithContext<FAQPage> => {
  const questions: Question[] =
    deck.cards?.map((card) =>
      createQuestion(card.front, card.back, {
        learningResourceType: "Flashcard",
        subject: deck.category,
      })
    ) ?? [];

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    description:
      deck.description ||
      `Study ${deck.name} with ${
        deck.cards?.length ?? 0
      } interactive flashcards`,
    mainEntity: questions,
    name: `${deck.name} Flashcard Deck`,
    publisher: organization,
    url: `${BASE_URL}/flashcards/${deck.id}`,
    ...createEducationalProperties({
      learningResourceType: "Flashcard Deck",
      subject: deck.category,
    }),
    dateCreated: deck.created_at ?? undefined,
    dateModified: deck.updated_at ?? undefined,
  };
};

const StructuredData = ({
  structuredData,
}: {
  structuredData: Thing | Thing[];
}) => {
  return (
    <>
      {Array.isArray(structuredData) ? (
        structuredData.map((data, index) => (
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(data),
            }}
            key={index}
            type="application/ld+json"
          />
        ))
      ) : (
        <script
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData),
          }}
          type="application/ld+json"
        />
      )}
    </>
  );
};

export default StructuredData;
